import { Point } from 'components/atoms/chart/useChart'
import moment from 'moment'
import {
  ChartItem,
  ChartItemResponse,
  CryptoChart,
  GetRewardSummaryResponse,
  RewardsHistoryItem,
  RewardsHistoryItemWithId,
  RewardSummaryDataState,
} from 'components/features/rewards/rewardPage/store'
import { sortBy, keyByToMap } from 'helpers/lodashHelpers'
import { Big, BigSource } from 'big.js'

export const mapChartItemToPoints = (item: ChartItem): Point => {
  const date = moment(item.date).valueOf()
  return [date, item.amount]
}

export const normalizeCryptoChart = (usdInput: ChartItemResponse[], cryptoInput: ChartItemResponse[]) => {
  const usd = usdInput.map(({ amount, date }) => ({
    date,
    amount: parseFloat(amount),
  }))

  return {
    usd,
    crypto: cryptoInput.map(({ amount, date }, index) => ({
      date,
      amount: parseFloat(amount) + usd[index].amount,
    })),
  }
}

const mergeNewPoints = (prevPoints: ChartItem[], newPoints: ChartItem[], dateMap: Map<string, ChartItemResponse>) => {
  const filtered = prevPoints.filter(({ date }) => !dateMap.has(date))
  return sortBy([
    ...filtered,
    ...newPoints,
  ], 'date')
}

export const mergeChartNewPoints = (chart: CryptoChart, usd: ChartItemResponse[], crypto: ChartItemResponse[]) => {
  const dateMap = keyByToMap(usd, 'date')
  const normalizedPoints = normalizeCryptoChart(usd, crypto)

  return {
    usd: mergeNewPoints(chart.usd, normalizedPoints.usd, dateMap),
    crypto: mergeNewPoints(chart.crypto, normalizedPoints.crypto, dateMap),
  }
}

export const normalizeRewardItem = (item: RewardsHistoryItem): RewardsHistoryItemWithId => ({
  ...item,
  uniqKey: item.transaction_type === 'crypto' ? item.uid : `${ item.id }`,
})

type NS = (payload: Partial<GetRewardSummaryResponse>) => RewardSummaryDataState
export const normalizeSummary: NS = ({
  rewards_in_progress_count,
  rewards_total_amount,
  usd_balance,
  crypto_balance,
  autoinvest_ticker,
  is_autoinvest_on,
  trend,
  lifetime_earnings,
  is_pending_crypto_order_exists,
}) => ({
  ...(rewards_in_progress_count !== undefined) && { rewards_in_progress_count },

  ...(rewards_total_amount !== undefined) && { rewardsTotalAmount: +rewards_total_amount },
  ...(usd_balance !== undefined) && { usdBalance: +usd_balance },
  ...(crypto_balance !== undefined) && { cryptoBalance: +crypto_balance },

  ...(autoinvest_ticker !== undefined) && { autoinvest_ticker },
  ...(is_autoinvest_on !== undefined) && { is_autoinvest_on },

  ...(trend !== undefined) && { trend: normalizeCryptoChart(trend.usd, trend.crypto) },
  ...(lifetime_earnings !== undefined) && { lifetime_earnings: normalizeCryptoChart(lifetime_earnings.rewards_total, lifetime_earnings.crypto_profit) },

  ...(is_pending_crypto_order_exists !== undefined) && { is_pending_crypto_order_exists },
} as RewardSummaryDataState)


export const getBalanceUsd = (amount: BigSource, price: BigSource) => {
  return new Big(amount).mul(price).toFixed(2, 0)
}

export const getMinimalAmountUsd = (crypto: BigSource, price: BigSource) => {
  return new Big(crypto).mul(price).toFixed(2, 3)
}

export const getAmountCrypto = (sellUsd: BigSource, balanceCrypto: BigSource, price: BigSource) => {
  const displayBalanceUsd = new Big(balanceCrypto).mul(price).toFixed(2, 0)
  const percent = new Big(sellUsd).div(displayBalanceUsd)
  return new Big(balanceCrypto).mul(percent).toFixed(8, 0).valueOf()
}
